import { useEffect, useState } from 'react';
import { APP_ROLES } from '@erp-attach/shared';
import type { AppRole } from '@erp-attach/shared';
import { useApi } from '../../api/ApiProvider';
import { useApiQuery } from '../../api/useApiQuery';
import type { RoleMapping } from '../../api/types';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { Spinner } from '../../components/ui/Spinner';

export function RoleMappingsAdmin() {
  const api = useApi();
  const query = useApiQuery((client, signal) => client.admin.roleMappings(signal), []);
  const [rows, setRows] = useState<RoleMapping[]>([]);
  const [newCode, setNewCode] = useState('');
  const [newRole, setNewRole] = useState<AppRole>('Sales.Branch');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data) setRows(query.data);
  }, [query.data]);

  const dirty = JSON.stringify(rows) !== JSON.stringify(query.data ?? []);

  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const result = await api.admin.putRoleMappings(rows);
      setRows(result);
      setSaved(true);
      query.reload();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    const code = newCode.trim().toUpperCase();
    if (!code || rows.some((r) => r.fusionRoleCode === code && r.appRole === newRole)) return;
    setRows((r) => [...r, { fusionRoleCode: code, appRole: newRole }]);
    setNewCode('');
  };

  return (
    <Card
      title="Fusion role → app role"
      actions={
        <Button size="sm" disabled={!dirty} busy={busy} onClick={() => void save()}>
          Save changes
        </Button>
      }
    >
      <p className="mb-3 text-xs text-muted-foreground">
        Every change is audited. A Fusion role can map to more than one app role; users get the union. Changes reach
        signed-in users on the next RoleSync (15 minutes).
      </p>
      {query.loading ? (
        <Spinner label="Loading role mappings" />
      ) : query.error ? (
        <ErrorBanner error={query.error} />
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="gsl-th">Fusion role code</th>
                <th className="gsl-th">App role</th>
                <th className="gsl-th"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.fusionRoleCode}-${r.appRole}`} className="gsl-row border-b border-border last:border-b-0">
                  <td className="px-4 py-2 font-mono text-xs">{r.fusionRoleCode}</td>
                  <td className="px-4 py-2">{r.appRole}</td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      icon="x"
                      iconOnly
                      aria-label={`Remove mapping ${r.fusionRoleCode} to ${r.appRole}`}
                      onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <form
            className="mt-4 flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              add();
            }}
          >
            <label className="block text-xs">
              <span className="font-medium text-muted-foreground">Fusion role code</span>
              <input
                className="gsl-input mt-1 !py-1.5 font-mono text-sm"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                placeholder="ORA_AR_CREDIT_ANALYST_JOB"
              />
            </label>
            <label className="block text-xs">
              <span className="font-medium text-muted-foreground">App role</span>
              <select className="gsl-input mt-1 !py-1.5 text-sm" value={newRole} onChange={(e) => setNewRole(e.target.value as AppRole)}>
                {APP_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <Button type="submit" variant="secondary" size="sm" disabled={newCode.trim().length === 0}>
              Add mapping
            </Button>
          </form>
          <p role="status" aria-live="polite" className="mt-2 text-xs text-pine">
            {saved ? 'Saved.' : ''}
          </p>
          {error ? <ErrorBanner error={error} /> : null}
        </>
      )}
    </Card>
  );
}
